import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';
import { toast } from 'sonner';

const RegistrationForm = ({ location, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    jobPosition: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  
  const [jobPositions, setJobPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchJobPositions();
  }, [location]);

  const fetchJobPositions = async () => {
    try {
      const response = await axios.get(`${API}/settings/job-positions?location=${location}`);
      setJobPositions(response.data || []);
    } catch (error) {
      console.error('Error fetching job positions:', error);
      toast.error('Nie udało się pobrać listy stanowisk');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Imię jest wymagane';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Nazwisko jest wymagane';
    }

    if (!formData.birthDate) {
      newErrors.birthDate = 'Data urodzenia jest wymagana';
    }

    // jobPosition is now optional - no validation needed

    if (!formData.username.trim() || formData.username.length < 3) {
      newErrors.username = 'Login musi mieć min. 3 znaki';
    }

    // Email validation — optional but must be valid if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Nieprawidłowy adres email';
    }

    if (!formData.password || formData.password.length < 4) {
      newErrors.password = 'Hasło musi mieć min. 4 znaki';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Hasła nie są identyczne';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Wypełnij poprawnie wszystkie pola');
      return;
    }

    setLoading(true);

    console.log('📤 Wysyłam rejestrację z lokacją:', location);

    try {
      const response = await axios.post(`${API}/manager/register`, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        birthDate: formData.birthDate,
        jobPosition: formData.jobPosition || null,
        email: formData.email.trim().toLowerCase() || null,
        username: formData.username.trim(),
        password: formData.password,
        location
      });

      console.log('✅ Rejestracja wysłana pomyślnie');
      console.log('📍 Lokacja w request:', location);

      // axios automatically parses JSON
      const data = response.data;

      toast.success(data.message || '✅ Rejestracja wysłana! Poczekaj na zatwierdzenie przez managera.');
      
      if (onSuccess) {
        onSuccess();
      }
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Nie udało się wysłać rejestracji';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <div className="registration-form-container">
      <div className="registration-form-header">
        <h2>📝 Rejestracja pracownika</h2>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>

      <form onSubmit={handleSubmit} className="registration-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">Imię *</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={errors.firstName ? 'error' : ''}
              placeholder="Jan"
              disabled={loading}
            />
            {errors.firstName && <span className="error-text">{errors.firstName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Nazwisko *</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={errors.lastName ? 'error' : ''}
              placeholder="Kowalski"
              disabled={loading}
            />
            {errors.lastName && <span className="error-text">{errors.lastName}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="birthDate">Data urodzenia *</label>
            <input
              type="date"
              id="birthDate"
              name="birthDate"
              value={formData.birthDate}
              onChange={handleChange}
              className={errors.birthDate ? 'error' : ''}
              disabled={loading}
            />
            {errors.birthDate && <span className="error-text">{errors.birthDate}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="jobPosition">Stanowisko (opcjonalne)</label>
            <select
              id="jobPosition"
              name="jobPosition"
              value={formData.jobPosition}
              onChange={handleChange}
              className={errors.jobPosition ? 'error' : ''}
              disabled={loading}
            >
              <option value="">Wybierz stanowisko (opcjonalne)</option>
              {jobPositions.map(position => (
                <option key={position.id} value={position.id}>
                  {position.name}
                </option>
              ))}
            </select>
            {errors.jobPosition && <span className="error-text">{errors.jobPosition}</span>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="email">
            Email (opcjonalnie){' '}
            <span style={{ fontSize: 11, color: '#d4af37', fontWeight: 400 }}>
              — użyj tego samego adresu do logowania przez Google
            </span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? 'error' : ''}
            placeholder="twoj.email@gmail.com"
            disabled={loading}
            autoComplete="email"
          />
          {errors.email && <span className="error-text">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="username">Login *</label>
          <input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            className={errors.username ? 'error' : ''}
            placeholder="jan.kowalski"
            disabled={loading}
            autoComplete="off"
          />
          {errors.username && <span className="error-text">{errors.username}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="password">Hasło *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
              placeholder="Min. 4 znaki"
              disabled={loading}
              autoComplete="new-password"
            />
            {errors.password && <span className="error-text">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Powtórz hasło *</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={errors.confirmPassword ? 'error' : ''}
              placeholder="Powtórz hasło"
              disabled={loading}
              autoComplete="new-password"
            />
            {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
          </div>
        </div>

        <div className="form-info">
          <p>ℹ️ Po wysłaniu formularza, twoje konto będzie oczekiwało na zatwierdzenie przez managera.</p>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onClose} disabled={loading} className="btn-cancel">
            Anuluj
          </button>
          <button type="submit" disabled={loading} className="btn-submit">
            {loading ? '⏳ Wysyłanie...' : '📤 Wyślij rejestrację'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;
